/**
 * TrialStrip — the trial's state and, crucially, its PRICE, visible without
 * anyone going looking (CLA-1010).
 *
 * ## Why
 *
 * In-app, price used to be discoverable but never volunteered. `UpgradePanel`
 * is purely reactive: it opens on a `claro:plan-required` event (a 402 or 403)
 * or on a click on a locked affordance. A trialing clinician who never hits a
 * wall saw no number at all until the trial-ending email at ~day 27 — which is
 * how Dr Brandon Kober-Brown got through signup and a trial knowing the 30/30
 * offer and nothing about what came after.
 *
 * ## Scope — web only, deliberately
 *
 * The App Store posture (CLA-849) is that iOS shows plan, status and usage but
 * **never prices or purchase UI**. Do not port this to iOS or Android. That
 * asymmetry is exactly why the welcome email (CLA-1007) is the load-bearing fix
 * and this is the supporting one.
 *
 * ## Price formatting
 *
 * Comes from `plan-pricing`, never re-derived here. `shortPriceLine` carries the
 * CLA-937 base-vs-seat rule: a base-price plan is quoted at the CLINIC price, so
 * Clinic reads A$349/mo and not the A$149 additional-practitioner rate (the
 * CLA-961 bug). "From" is the cheapest public plan, which is the honest floor.
 */
import { Link } from 'react-router'
import { usePlans, useSubscription, type Plan } from '@/api/subscription-hooks'
import { shortPriceLine } from '@/lib/plan-pricing'

function daysLeft(endsAt: string | null | undefined): number | null {
  if (!endsAt) return null
  const ms = new Date(endsAt).getTime() - Date.now()
  if (!Number.isFinite(ms)) return null
  return Math.max(0, Math.ceil(ms / 86_400_000))
}

/** The cheapest public priced plan — the honest "from" floor. */
function cheapestPriceLine(plans: Plan[] | undefined): string | null {
  if (!plans?.length) return null

  // No is_public filter: GET /subscription/plans is already public-only
  // server-side (`list_plans(public_only: true)`), so internal plans never
  // arrive here and re-filtering would just duplicate the rule.
  const priced = plans
    .map((p) => {
      const base = p.monthly_base_price ?? p.monthly_seat_price
      const amount = base == null ? null : Number(base)
      return { plan: p, amount }
    })
    .filter((x): x is { plan: Plan; amount: number } => {
      return x.amount != null && Number.isFinite(x.amount) && x.amount > 0
    })
    .sort((a, b) => a.amount - b.amount)

  return priced.length ? shortPriceLine(priced[0].plan, 'monthly') : null
}

export function TrialStrip({ onNavigate }: { onNavigate?: () => void }) {
  const { data: sub } = useSubscription()
  // Only fetched to price the line; the strip renders without it rather than
  // waiting, so a slow plans call never blanks the countdown.
  //
  // Gated on actually being in a trial (CLA-1170). This strip mounts on every
  // page but returns null below for anyone not `trialing`, so an unconditional
  // fetch spent one authenticated read on every cold boot, for every user who
  // is not on a trial, and threw the answer away. Waiting for `sub` to resolve
  // costs the trialing case a round trip, which the comment above already
  // accepts: the countdown renders without the price either way.
  const { data: plansData } = usePlans({ enabled: sub?.status === 'trialing' })

  // Fail quiet: anything other than an actual trial renders nothing. A billing
  // surface that guesses is worse than one that is absent.
  if (!sub || sub.status !== 'trialing') return null

  const days = daysLeft(sub.trial_ends_at)
  const usage = sub.note_usage
  const from = cheapestPriceLine(plansData?.plans)

  const parts: string[] = []
  if (usage && typeof usage.used === 'number' && typeof usage.cap === 'number') {
    parts.push(`${usage.used} of ${usage.cap} notes`)
  }
  if (days != null) parts.push(days === 1 ? '1 day left' : `${days} days left`)

  // Nothing meaningful to say — don't render a bare chrome box.
  if (!parts.length) return null

  return (
    <div className="px-4 py-2 border-t border-border-subtle">
      <Link
        to="/settings/plan"
        onClick={onNavigate}
        className="block group"
        title="Trial usage and plans"
      >
        <p className="text-[11px] font-medium text-ink-secondary group-hover:text-ink transition-colors">
          Trial · {parts.join(' · ')}
        </p>
        {from && (
          <p className="text-[10px] text-ink-faint group-hover:text-ink transition-colors">
            then from {from} ex-GST
          </p>
        )}
      </Link>
    </div>
  )
}
